/**
 * StepUpSurface — the canonical re-authentication dialog body
 * shared across the platform admin console, the tenant admin
 * console, and the end-user portal.
 *
 * Mirror in spirit of `LoginSurface`: the state machine, the
 * TransportPicker / CeremonyPrompt rendering, the cancel handling,
 * and the error messaging are owned here. Each consuming app
 * supplies only what is NOT universal:
 *
 *   - which step-up endpoint to call (admin vs portal — the body
 *     shape is identical because the backend handler is the same
 *     `session.StepUpHandler`, but the route mount differs)
 *   - the per-app Zustand store hooks (`pending`, `markVerified`,
 *     `cancel`, optional `failPending` for the portal which doesn't
 *     surface AuthError separately)
 *
 * Everything else — the picker initial state resolved via the
 * `useCeremonyTransport` hook (localStorage → server-side
 * recommendation → product default), the in-flight CeremonyPrompt
 * variant matching, the on-cancel reducer reset, the dialog
 * chrome — is identical across all three surfaces and lives here.
 *
 * # Why a dialog and not a page
 *
 * Step-up is invoked mid-action (a click on a mutation button
 * trips a 403 step-up-required, the wrapper opens this dialog,
 * the user verifies, the wrapper retries the original mutation).
 * A page-level surface like LoginSurface would lose the calling
 * context. The dialog wraps the same primitives in a
 * `<GradientDialog>` so the visual identity is consistent with
 * sign-in but the interaction surface is overlay rather than
 * navigation.
 *
 * # Body states, and they all have the SAME SHAPE
 *
 * A step-up ceremony must look like itself everywhere. Recognisability is most of how somebody tells
 * a genuine passkey prompt from a page imitating one, so the dialog's furniture — button, hint line,
 * switch-device row — is constant. What changes is the label, the enabled state, and (only when the
 * user asks) the picker.
 *
 *
 *   streamlined  → DEFAULT for a fresh open. The dialog already
 *                  knows WHO the user is (active session) and
 *                  which transport they last used (the four-layer
 *                  cascade in useCeremonyTransport). Showing the
 *                  full 3-card picker every time forces the user
 *                  to re-confirm a choice they've already made
 *                  every other surface. Streamlined view renders
 *                  a single transport-aware Verify button + a
 *                  secondary "Use another device" affordance for
 *                  the rare case where the worker wants to switch.
 *                  Matches Okta / Auth0 / Entra step-up UX.
 *
 *   picker       → TransportPicker (the canonical 3-card chooser).
 *                  Surfaced ONLY via the secondary affordance —
 *                  the user asking for it. It used to appear by
 *                  itself on ceremony failure, which meant the
 *                  step-up dialog looked like one thing in most
 *                  places and a different thing after a failure.
 *                  A security ceremony that changes shape is
 *                  harder to recognise, and recognisability is
 *                  most of how someone tells a genuine passkey
 *                  prompt from a fake one. The saved click was
 *                  not worth that.
 *
 *   running      → CeremonyPrompt (the pulsing-icon "Waiting for
 *                  your authenticator" panel; variant matches the
 *                  chosen transport so an admin who picked phone
 *                  sees "Scan the QR code with your phone")
 *
 * # User-activation invariant
 *
 * The dialog opens in response to a mutation 403 — by the time
 * the dialog is mounted, user activation MAY have expired (slow
 * round-trip). We deliberately do NOT auto-trigger the ceremony
 * on open. The Verify button click is the user activation that
 * gates navigator.credentials.get. This is the only difference
 * from "click a single button" being indistinguishable from
 * auto-trigger in practice — the click is required for security
 * and for fresh activation regardless of network latency.
 */

import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import type { ReactNode } from "react";
import { AlertTriangle, ArrowRight, KeyRound, ShieldCheck, Smartphone, Usb } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import {
  AuthError,
  CeremonyError,
  StepUpFailedError,
  ceremonyReducer,
  classifyCeremonyFailure,
  createCeremonyId,
  initialCeremonyState,
  attemptedTransportFor,
  defaultCapabilitySnapshot,
  parseUaHint,
  probeCapabilities,
  isPasskeyTransport,
  reportCeremonyEvent,
  runAssertion,
  type AssertionOptionsWire,
  type AssertionResultWire,
  type CapabilitySnapshot,
  type CeremonyClassification,
  type PasskeyTransport,
  type UaHint,
} from "@pureid/auth-core";
import { Button } from "../button";
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../dialog";
import { GradientDialog } from "../theme";
import { IconBadge } from "../icon-badge";
import {
  isTransportViable,
  useCeremonyTransport,
} from "../../hooks/use-ceremony-transport";
import { CeremonyError as CeremonyErrorView } from "./ceremony-error";
import type { CeremonyPromptVariant } from "./ceremony-prompt";
import { CeremonyPrompt } from "./ceremony-prompt";
import { TransportPicker } from "./transport-picker";
import { RecommendedDeviceChip } from "../recommended-device-chip";
import {
  narratorSlotFor,
  resolveSlot,
  type RecommendedDefault,
} from "../../lib/onboarding-slots";

/**
 * StepUpClient is the per-app contract for the two ceremony
 * round-trips. Each consuming app supplies an implementation that
 * targets its own step-up endpoint mount; the shared surface
 * orchestrates them with the canonical reducer + runner.
 *
 * `begin` is responsible for POSTing
 * `/api/ceremony/v1/authentication/begin` (with whatever scope and
 * tenant headers the app needs) and returning the ceremony id +
 * the wire-shape options the browser ceremony will consume.
 *
 * `complete` is responsible for POSTing the verify endpoint
 * (`/api/admin/v1/sessions/step-up` for admin, `/api/me/v1/sessions/step-up`
 * for portal) with the assertion result. Returns void on success;
 * throws AuthError on session-died-mid-ceremony so the surface can
 * propagate it up.
 *
 * The `hint` argument is the WebAuthn L3 `hints` array value
 * derived from the user's chosen transport — `"hybrid"`,
 * `"client-device"`, or `"security-key"`. The shared
 * `transportToHint` helper exported below maps PasskeyTransport
 * (UI vocabulary) to that array.
 */
export interface StepUpClient {
  /**
   * The optional second argument carries the per-flow client
   * ceremony correlation UUID minted by `StepUpSurface` at
   * mount. Implementations should forward it to apiFetch as
   * the `X-Ceremony-ID` header so server-side log lines and
   * audit rows for the begin call join the matching browser-
   * side telemetry events under one search key.
   *
   * Implementations may ignore the option entirely (it's
   * optional) — the surface's ceremony id propagation is
   * best-effort.
   */
  begin: (
    hint: string,
    opts?: { clientCeremonyId?: string },
  ) => Promise<{
    ceremonyId: string;
    options: AssertionOptionsWire;
  }>;
  /** See `begin` for the `clientCeremonyId` contract. */
  complete: (
    ceremonyId: string,
    assertion: AssertionResultWire,
    opts?: { clientCeremonyId?: string },
  ) => Promise<void>;
}

/**
 * Store contract — the shared surface subscribes via these hooks
 * rather than reaching into a shared singleton, so each app's
 * Zustand store stays per-app (it carries app-local error types,
 * api-client refs, etc.).
 */
export interface StepUpStoreBinding {
  /** Whether a step-up challenge is currently pending. */
  pending: boolean;
  /** Optional reason hint surfaced in the dialog subtitle. */
  reason?: string;
  /** Resolve the pending promise as verified — closes the dialog. */
  markVerified: () => void;
  /** Reject the pending promise as cancelled — closes the dialog. */
  cancel: () => void;
  /** Optional: reject the pending promise with a non-cancel error.
   *  The admin store carries this for AuthError propagation
   *  during a session-died-mid-ceremony; portal store can omit. */
  failPending?: (err: Error) => void;
}

export interface StepUpSurfaceProps {
  client: StepUpClient;
  store: StepUpStoreBinding;
  /**
   * Optional API origin for `/api/client-telemetry`. When
   * supplied, the dialog mints a ceremony correlation UUID
   * the moment the dialog opens, probes the browser's
   * capability snapshot, classifies failures with
   * `classifyCeremonyFailure`, emits fire-and-forget
   * telemetry events at every ceremony moment, and renders
   * the canonical `<CeremonyError>` primitive on failure
   * (richer remedy + diagnostic disclosure than the legacy
   * inline error banner).
   *
   * Empty / undefined → telemetry layer is disabled and the
   * dialog falls back to the legacy inline error banner.
   * Backwards-compatible.
   */
  apiBaseUrl?: string;
  /**
   * Optional server-side onboarding recommendation. When set,
   * the dialog brings the DD-2 device-hint chip + DD-3
   * narrator into the step-up surface — the same language the
   * user already saw at sign-in / enrollment, so a mid-action
   * verify reads as familiar territory rather than a fresh
   * uncontextualised prompt.
   *
   *   - DD-2 chip renders in the dialog header on the picker
   *     state (idle, no failure).
   *   - DD-3 narrator renders beneath the CeremonyPrompt
   *     during the in-flight state. The narrator slot is
   *     derived from (chosen transport, recommendation's
   *     device-hint slot) via the shared `narratorSlotFor`
   *     helper — same mapping LoginSurface uses.
   *
   * Undefined → both decorations are absent (legacy bare
   * step-up). Backwards-compatible.
   */
  recommendedDefault?: RecommendedDefault;
}

// Transport→telemetry mapping is the shared `attemptedTransportFor`
// from @pureid/auth-core (single source of truth across the
// enrollment wizard, recovery, the portal add-a-passkey dialog,
// and this step-up surface).

function transportToCeremonyVariant(t: PasskeyTransport): CeremonyPromptVariant {
  // Exhaustive over PasskeyTransport. Step-up is a re-auth
  // ceremony — it only ever runs a real transport, never the
  // pureid_qr enrollment flow-channel (absent by type).
  switch (t) {
    case "other_device":
      return "hybrid";
    case "this_device":
      return "platform";
    case "security_key":
      return "any";
  }
}

export function StepUpSurface({
  client,
  store,
  apiBaseUrl,
  recommendedDefault,
}: StepUpSurfaceProps): ReactNode {
  const [ceremony, dispatch] = useReducer(ceremonyReducer, initialCeremonyState);

  // `running` = the in-flight transport, used to drive the
  // CeremonyPrompt variant. null = idle (either streamlined or
  // picker visible).
  const [running, setRunning] = useState<PasskeyTransport | null>(null);

  // `showPicker` toggles between the streamlined single-button
  // view (the post-2026-05-26 default — we already know the
  // transport from the four-layer cascade) and the full 3-card
  // TransportPicker. False by default on a fresh open; flipped
  // true by:
  //   - the user clicking "Use another device" in the streamlined
  //     view (explicit opt-in to switch transport)
  //   - a ceremony failure (auto-recovery: the user needs to
  //     try a different transport, picker is the right surface)
  //
  // The flag is per-dialog-open, not persisted, so a worker who
  // toggles to picker for one verification doesn't see the
  // picker forever after — next step-up opens streamlined again.
  const [showPicker, setShowPicker] = useState(false);

  // Wrapper ref for the picker subtree — used to pull focus into
  // the picker's first interactable when a failure flips
  // showPicker. The picker is a complex subtree (3 cards + a
  // continue button + extra affordances depending on tenant
  // policy); rather than thread a ref through every internal
  // element, we wrap the picker in a div and query the first
  // focusable child. Cheap and stable across picker refactors —
  // adding a new TransportPicker prop just to forward a ref for
  // ONE caller would be inversion of the surface contract.
  const pickerContainerRef = useRef<HTMLDivElement | null>(null);

  // pickerMountTrigger bumps whenever AnimatePresence finishes
  // an exit animation. With mode="wait", the outgoing body
  // (streamlined / running) completes its exit BEFORE the new
  // body (picker) mounts; without this trigger the focus-pull
  // useEffect would fire on the showPicker dep change and find
  // pickerContainerRef.current === null because the picker
  // isn't mounted yet. By bumping the trigger from
  // `onExitComplete` and including it in the effect deps, the
  // effect re-runs after the picker has actually been rendered.
  // Counter (not boolean) so consecutive transitions don't
  // collapse via React's bail-on-identical-value optimization.
  const [pickerMountTrigger, setPickerMountTrigger] = useState(0);

  // When showPicker flips true after a failure (or via the user
  // clicking "Use another device"), the streamlined Verify
  // button unmounts and focus falls back to the dialog root.
  // Programmatically restore focus to the picker's first
  // interactable so keyboard users keep the focus thread and
  // screen-reader users hear the new options announced.
  //
  // Only fires when transitioning to picker DURING an open
  // dialog AND when no ceremony is running. The open-cycle
  // reset effect above also clears showPicker to false; the
  // running check prevents the focus pull from racing the
  // ceremony-prompt render path. The focus query intentionally
  // skips `tabindex="-1"` elements so the chip / icon-badge
  // decorations don't capture the focus thread.
  //
  // pickerMountTrigger is in the deps so the effect re-runs
  // after AnimatePresence's exit completes and the picker is
  // actually mounted (see pickerMountTrigger comment above).
  useEffect(() => {
    if (!store.pending) return;
    if (running !== null) return;
    if (!showPicker) return;
    const root = pickerContainerRef.current;
    if (!root) return;
    const focusable = root.querySelector<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    focusable?.focus();
  }, [showPicker, store.pending, running, pickerMountTrigger]);

  // Reduced-motion is honored cooperatively with motion/react:
  // when `useReducedMotion()` returns true every transition
  // duration collapses to 0 so swaps are instant — including
  // the layout-height animation below, which must collapse in
  // lockstep with the opacity fades or reduced-motion users
  // see an instant content swap but an animated height resize
  // (the worst of both modes).
  //
  // Motion-language vocabulary — three transitions:
  //
  //   - bodyEnter: ease-out, 220ms. The new body is the
  //     user's destination; they're paying attention. Smooth
  //     decelerated arrival with a slight settle reads as
  //     "the surface has resolved" rather than "snap, here's
  //     content."
  //   - bodyExit: ease-in, 120ms. The old body is leaving;
  //     it's already-seen content. Accelerate out of the way
  //     so the dialog's vertical real estate is freed for the
  //     arrival without making the user wait. Asymmetric
  //     timing matches Material's "emphasized" motion pattern
  //     and the codebase's AlertBanner exit (200ms-class).
  //   - layoutTransition: ease-out, 220ms — same envelope as
  //     bodyEnter. The dialog grows / shrinks to fit the new
  //     body in lockstep with the new body fading in, so the
  //     user perceives ONE arrival animation instead of two
  //     parallel ones (fade + resize).
  //
  // The shared 0.16/1/0.3/1 ease in bodyEnter + layoutTransition
  // mirrors the codebase's AnimateIn (350ms) so the step-up
  // surface reads as part of the same motion family even
  // though step-up uses the shorter state-transition window
  // appropriate to a re-render rather than an entrance.
  const prefersReducedMotion = useReducedMotion();
  const bodyEnter = {
    duration: prefersReducedMotion ? 0 : 0.22,
    ease: [0.16, 1, 0.3, 1] as const,
  };
  const bodyExit = {
    duration: prefersReducedMotion ? 0 : 0.12,
    ease: [0.4, 0, 1, 1] as const,
  };
  const layoutTransition = bodyEnter;

  // ── Diagnostics ───────────────────────────────────────────
  //
  // Mint a fresh ceremony correlation UUID per dialog OPEN
  // (not per surface mount — the dialog can open and close
  // many times in a single session). The ref stores the id
  // for the duration of one open cycle; we re-mint when the
  // dialog transitions from closed → open. The capability
  // snapshot is probed once per surface and cached.
  //
  // Diagnostics are gated on `apiBaseUrl` being supplied;
  // absent → the legacy inline error banner renders on
  // failure and no telemetry fires. Backwards-compatible.
  const clientCeremonyIdRef = useRef<string>("");
  const ua = useMemo<UaHint>(
    () => parseUaHint(typeof navigator !== "undefined" ? navigator.userAgent : null),
    [],
  );
  const [capability, setCapability] = useState<CapabilitySnapshot | null>(null);
  const [classification, setClassification] = useState<CeremonyClassification | null>(null);

  // Transport state — same shared hook LoginSurface uses, so a
  // user's prior choice (saved on every successful sign-in /
  // enrollment) preselects the matching card here, AND a step-up
  // pick is persisted on success so the next surface in line
  // sees it too. One consistent affordance across every passkey
  // surface in PureID.
  //
  // Capability is threaded in (2026-05-22) so the layer-4
  // fallback degrades off mobile-first on no-BLE hosts, matching
  // LoginSurface byte-for-byte. The QR-not-showing issue is still
  // fixed at the BACKEND (dedicated step-up begin endpoint that
  // uses non-discoverable mode + transport filter), not by
  // gimmicking the picker default here — the capability gating
  // exists so the picker doesn't seed onto a card that's about
  // to dead-end, not to prefer one transport over another. Server
  // preference is deliberately omitted: step-up runs mid-session
  // and doesn't carry /identify's KnownUserResult, so localStorage
  // is the personal-pick signal for this surface. The hook's FU-5
  // late-arrival useEffect re-runs the cascade when the probe
  // resolves, so the picker re-seeds once we know — gated on
  // hasPickedRef so a user who has clicked keeps their pick.
  // Re-authentication begins on the browser the person is already using. Unlike first-time
  // enrollment, step-up must not manufacture a phone/QR recommendation merely because hybrid is
  // available: that made ordinary application-access approvals say "Use my phone" while the
  // action-bound agent approval beside them correctly opened the current-device authenticator.
  // A transport the person successfully chose earlier still wins through localStorage, and the
  // picker remains available for an explicit switch.
  const transport = useCeremonyTransport("this_device", {
    capability,
    ignoreRememberedPreference: true,
  });
  // Wall-clock start of the in-flight ceremony, for the
  // FAST_FAIL_THRESHOLD_MS heuristic in the classifier.
  const ceremonyStartRef = useRef<number>(0);

  // Capability probe runs UNCONDITIONALLY — the snapshot feeds
  // the transport hook's layer-4 capability-aware fallback (see
  // `useCeremonyTransport("this_device", { capability })` above) and
  // surfaces that need it shouldn't be gated on whether telemetry
  // is wired. Telemetry emission below still checks apiBaseUrl
  // separately; the two responsibilities just no longer share a
  // gate.
  //
  // The probe's inner steps (probePlatformAuthenticator,
  // probeConditionalMediation, probeHybridFeasibility) return
  // null on missing APIs rather than rejecting, but the
  // underlying `PublicKeyCredential.isUVPAA()` is browser-
  // implemented and could throw on edge-case agents. Falling
  // back to defaultCapabilitySnapshot() on reject guarantees
  // `capability` resolves to a non-null value — without this,
  // a probe rejection would hang the streamlined-view gate's
  // ProbingPlaceholder forever.
  useEffect(() => {
    let cancelled = false;
    probeCapabilities()
      .then((snap) => {
        if (!cancelled) setCapability(snap);
      })
      .catch(() => {
        if (!cancelled) setCapability(defaultCapabilitySnapshot());
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // `succeededRef` tracks whether the dialog closed via
  // store.markVerified() (success) so the abandon event in
  // the cleanup branch knows whether to fire. The flag
  // resets on every fresh open.
  const succeededRef = useRef(false);

  // Mint a fresh id (and emit preflight + surface_entered
  // telemetry) every time the dialog opens. On close, emit
  // surface_abandoned IFF the dialog didn't reach success.
  // Closing clears the id so a stale value can never bleed
  // across opens.
  useEffect(() => {
    if (!apiBaseUrl) return;
    if (!store.pending) {
      // Dialog closed. The cleanup of the previous "open"
      // effect-run handles the abandon event; here we just
      // clear state so the next open starts fresh.
      clientCeremonyIdRef.current = "";
      setClassification(null);
      return;
    }
    const id = createCeremonyId();
    clientCeremonyIdRef.current = id;
    succeededRef.current = false;
    if (capability) {
      reportCeremonyEvent(
        { baseUrl: apiBaseUrl, ceremonyId: id },
        {
          phase: "preflight",
          outcome: "ok",
          code: "ok",
          capability,
          ua,
        },
      );
    }
    // Funnel-entry telemetry (P0d). Coarse surface label
    // distinguishes step-up from login/enroll abandonment in
    // dashboards.
    reportCeremonyEvent(
      { baseUrl: apiBaseUrl, ceremonyId: id },
      {
        phase: "surface_entered",
        outcome: "ok",
        code: "step_up",
        capability:
          capability ?? {
            webauthn: typeof window !== "undefined" && "PublicKeyCredential" in window,
            secureContext: typeof window !== "undefined" && window.isSecureContext === true,
            platformAuthenticator: null,
            conditionalMediation: null,
            hybridFeasibility: null,
            privateMode: null,
          },
        ua,
      },
    );

    return () => {
      // Dialog is closing. Emit surface_abandoned IFF the
      // close path didn't go through a successful verify.
      if (succeededRef.current) return;
      reportCeremonyEvent(
        { baseUrl: apiBaseUrl, ceremonyId: id },
        {
          phase: "surface_abandoned",
          outcome: "abort",
          code: "step_up",
          capability:
            capability ?? {
              webauthn: typeof window !== "undefined" && "PublicKeyCredential" in window,
              secureContext: typeof window !== "undefined" && window.isSecureContext === true,
              platformAuthenticator: null,
              conditionalMediation: null,
              hybridFeasibility: null,
              privateMode: null,
            },
          ua,
        },
      );
    };
  }, [apiBaseUrl, store.pending, capability, ua]);

  const errorMessage =
    ceremony.status === "error" ? ceremony.message : null;

  // Reset just the in-flight + reducer state on each fresh open —
  // the transport state is owned by the hook and persists across
  // dialog opens by design (the user's prior pick should preselect).
  // showPicker also resets so a worker who opted into the picker
  // on a previous step-up doesn't carry that toggle into the next
  // one — every fresh open starts from the streamlined default.
  // pickerMountTrigger NOT reset: it's a monotonic counter the
  // focus useEffect's dep array uses to re-run after exit
  // animations; resetting would have the same effect as bumping
  // (both flip the dep value), which would fire the focus pull
  // on every open even before the picker mounts.
  useEffect(() => {
    if (store.pending) {
      setRunning(null);
      setClassification(null);
      setShowPicker(false);
    }
  }, [store.pending]);

  async function run(t: PasskeyTransport): Promise<void> {
    if (!store.pending || running !== null) return;
    setRunning(t);
    setClassification(null);

    const clientId = clientCeremonyIdRef.current;
    const telemetryBase = apiBaseUrl && clientId ? apiBaseUrl : null;
    const transportTelem = attemptedTransportFor(t);
    const capForCeremony = capability ?? defaultCapabilitySnapshot();

    ceremonyStartRef.current = Date.now();
    if (telemetryBase) {
      reportCeremonyEvent(
        { baseUrl: telemetryBase, ceremonyId: clientId },
        {
          phase: "begin",
          outcome: "ok",
          code: "ok",
          transportAttempted: transportTelem,
          capability: capForCeremony,
          ua,
        },
      );
    }

    try {
      let ceremonyId = "";
      // The PasskeyTransport string IS the wire `hint` value the
      // backend's HintsFromUIPreference accepts — no client-side
      // translation. A previous implementation translated to the
      // WebAuthn L3 vocabulary (`client-device`, `hybrid`, …)
      // here, which the backend silently dropped to nil and
      // produced an unbiased OS picker.
      await runAssertion(
        dispatch,
        async () => {
          const res = await client.begin(t, { clientCeremonyId: clientId || undefined });
          ceremonyId = res.ceremonyId;
          return res.options;
        },
        async (assertion) =>
          client.complete(ceremonyId, assertion, {
            clientCeremonyId: clientId || undefined,
          }),
      );
      // Persist the winning transport — same lifecycle as
      // LoginSurface's success effect, so step-up choices and
      // sign-in choices reinforce each other across surfaces.
      if (telemetryBase) {
        reportCeremonyEvent(
          { baseUrl: telemetryBase, ceremonyId: clientId },
          {
            phase: "finish",
            outcome: "ok",
            code: "ok",
            transportAttempted: transportTelem,
            durationMs: Date.now() - ceremonyStartRef.current,
            capability: capForCeremony,
            ua,
          },
        );
      }
      transport.commit();
      // Mark success BEFORE store.markVerified() so the
      // pending-watcher effect's cleanup path sees the flag
      // when it runs and skips the abandon event.
      succeededRef.current = true;
      store.markVerified();
      dispatch({ type: "reset" });
    } catch (err) {
      const durationMs = Date.now() - ceremonyStartRef.current;
      if (err instanceof AuthError) {
        // Session died mid-ceremony. Reset the reducer so a
        // re-open doesn't show the stale failure, then propagate
        // the AuthError so the global SignedOutScreen takes over.
        dispatch({ type: "reset" });
        if (store.failPending) {
          store.failPending(err);
        } else {
          // Stores without failPending fall back to cancel —
          // the user re-lands on /login via their app's
          // standard 401 handling.
          store.cancel();
        }
      } else if (err instanceof StepUpFailedError) {
        // Step-up VERIFY rejected the assertion (wrong passkey,
        // user-binding mismatch, rate-limited). Main session is
        // still valid — show inline error and let the user retry
        // or switch transport. DO NOT route to SignedOutScreen.
        // The ceremony runner already dispatched 'reset' before
        // throwing; replay a 'fail' so the inline banner shows
        // a copy that matches the specific failure code.
        dispatch({
          type: "fail",
          code: "verify_failed",
          message: stepUpFailureCopy(err.code),
        });
        // THE CEREMONY DOES NOT CHANGE SHAPE ON FAILURE. This used to open the
        // picker automatically, on the reasoning that the usual fix for a rejected
        // verification is a different passkey and the picker makes that one click
        // instead of two. The click it saved cost something bigger: the step-up
        // dialog looked like one thing in most places and a different thing after a
        // failure, and a security ceremony that changes shape is harder to
        // recognise — recognisability is most of how someone tells a genuine
        // passkey prompt from a fake one. The "Use another device" link is still
        // right there, and it is now the only thing that opens the picker, so the
        // switch is the user's deliberate act rather than something the dialog did
        // to them mid-failure.
        // No CeremonyClassification for stepup verify failures
        // — the legacy inline copy is more specific (rate limit
        // vs user mismatch vs wrong passkey) than the generic
        // verify_failed bucket the rich classifier would
        // produce. Telemetry still captures the event so
        // operators see the funnel.
        if (telemetryBase) {
          reportCeremonyEvent(
            { baseUrl: telemetryBase, ceremonyId: clientId },
            {
              phase: "finish",
              outcome: "error",
              code: "verify_failed",
              errorName: "StepUpFailedError",
              errorMessage: err.code,
              transportAttempted: transportTelem,
              durationMs,
              capability: capForCeremony,
              ua,
            },
          );
        }
      } else if (telemetryBase) {
        // Other ceremony failure (DOMException from
        // navigator.credentials, network error, etc.).
        // Classify with the rich heuristic and surface
        // <CeremonyError> with the structured remedy.
        const cause = err instanceof CeremonyError ? (err.cause ?? err) : err;
        const classified = classifyCeremonyFailure({
          err: cause,
          durationMs,
          phase: "get",
          transport: transportTelem,
          capability: capForCeremony,
        });
        setClassification(classified);
        // Shape stays fixed here too — see the StepUpFailedError branch above.
        reportCeremonyEvent(
          { baseUrl: telemetryBase, ceremonyId: clientId },
          {
            phase: "get",
            outcome: "error",
            code: classified.code,
            errorName: classified.domException?.name,
            errorMessage: classified.domException?.message,
            transportAttempted: transportTelem,
            durationMs,
            capability: capForCeremony,
            ua,
          },
        );
      }
      // Legacy fall-through: when no telemetry base is wired AND the error wasn't
      // AuthError / StepUpFailedError, the runner has already dispatched 'fail'
      // into the reducer and the inline banner renders from ceremony.message. It
      // no longer opens the picker either — every failure path keeps one shape.
    } finally {
      setRunning(null);
    }
  }

  function handleCancel(): void {
    dispatch({ type: "reset" });
    setRunning(null);
    store.cancel();
  }

  // Picker-or-prompt body. When a classified failure is
  // present (rich classifier produced a CeremonyClassification)
  // we render <CeremonyError> ABOVE the picker so the user can
  // see the structured remedy + diagnostic disclosure AND still
  // pick a different transport without leaving the dialog. The
  // legacy inline-error banner stays as the fallback path for
  // surfaces that didn't wire apiBaseUrl.
  // DD-3 narrator slot for the in-flight prompt. Derived from the
  // chosen transport + the recommendation's device-hint slot
  // (mirrors how LoginSurface picks the narrator). When no
  // recommendation is supplied, no narrator renders — the
  // CeremonyPrompt's stock copy stays the only voice.
  const narratorText =
    running !== null && recommendedDefault
      ? resolveSlot(
          narratorSlotFor(running, recommendedDefault.deviceHintSlot),
        )
      : "";

  // Idle (non-running) body has three variants now:
  //
  //  - streamlined (default): a single Verify button for the
  //    preselected transport plus an "Use another device" link.
  //    The user has already authenticated this session; we know
  //    which transport they last used; we don't make them re-pick.
  //    Gated on capability !== null so we don't flicker the
  //    button label when the late-arrival useEffect in
  //    useCeremonyTransport re-runs the cascade with the
  //    resolved snapshot (see A2 in the architect review).
  //
  //  - probing (capability still resolving): a minimal placeholder.
  //    The capability snapshot drives BOTH the cascade's viability
  //    gate AND the streamlined button's transport-aware copy.
  //    Rendering the streamlined view before the probe resolves
  //    would cause the button label to flicker mid-render if the
  //    layer-1 (localStorage) pick fails the viability gate. The
  //    probe typically resolves in <100ms; the placeholder is
  //    barely perceptible and is strictly better than the flicker.
  //
  //  - picker (opt-in or failure-recovery): the full 3-card
  //    TransportPicker — the prior behavior. Surfaced when the
  //    user explicitly asks (via the streamlined view's link) or
  //    when a ceremony failure suggests they should switch. NOT
  //    capability-gated: the picker shows all three options
  //    regardless (the user explicitly asked to switch, so the
  //    cards' presence is the affordance — capability shapes
  //    the cascade's default selection, not the picker's
  //    visibility).
  //
  // The running state is unchanged from before — single CeremonyPrompt
  // variant + narrator slot.
  //
  // `probing` is true while we cannot yet render the streamlined
  // view without risking a button-label flicker. Two cases:
  //
  //   1. capability has not resolved yet (probe in flight).
  //   2. capability HAS resolved but the hook's transport.value
  //      is still the pre-resolution layer-1 pick that the
  //      viability gate would now reject. The hook's late-
  //      arrival useEffect commits the downgrade on the next
  //      tick; until then transport.value is stale by one frame.
  //      Without case 2, render N would show "Verify with this
  //      device" and render N+1 would show "Verify with your
  //      phone" — the exact flicker the probe gate was added to
  //      prevent.
  //
  // The picker view is intentionally NOT gated on `probing` — it
  // explicitly displays every option, and a brief stale-card
  // highlight is much less jarring than a button-label flip.
  const probing =
    capability === null || !isTransportViable(transport.value, capability);

  // Body-variant resolution: pick one of four mutually-exclusive
  // bodies and tag with a key AnimatePresence uses to drive
  // enter/exit animations. The order of these branches is the
  // priority — running > picker > probing > streamlined — and
  // mirrors the visual hierarchy (the in-flight ceremony's
  // CeremonyPrompt MUST appear immediately on Verify-click; the
  // failure-recovery picker MUST appear immediately on failure;
  // probing is the fall-through wait state; streamlined is the
  // happy-path default). Single source of truth so the body
  // computation can't drift from the key the animation layer
  // uses.
  type BodyKey = "running" | "picker" | "probing" | "streamlined";
  let bodyKey: BodyKey;
  let bodyContent: ReactNode;
  if (running !== null) {
    bodyKey = "running";
    bodyContent = (
      <div className="space-y-3">
        <CeremonyPrompt
          mode="signin"
          variant={transportToCeremonyVariant(running)}
        />
        {narratorText ? (
          <p
            className="text-center text-xs text-muted-foreground leading-relaxed"
            role="status"
            aria-live="polite"
          >
            {narratorText}
          </p>
        ) : null}
      </div>
    );
  } else if (showPicker) {
    bodyKey = "picker";
    bodyContent = (
      <div ref={pickerContainerRef}>
        {classification ? (
          <div className="mb-4">
            <CeremonyErrorView
              classification={classification}
              ceremonyId={clientCeremonyIdRef.current}
              capability={capability ?? undefined}
              ua={ua}
              onPrimary={() => void run(transport.value)}
              showSecondary={false}
            />
          </div>
        ) : (
          errorMessage && (
            <div
              className="mb-4 flex items-start gap-2 rounded-lg border border-danger/30 bg-danger/5 p-3 text-sm text-danger"
              role="alert"
              aria-live="assertive"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="min-w-0">
                <p>{errorMessage}</p>
                <p className="pt-1 text-xs text-muted-foreground">
                  If your passkey lives on a different device, switch
                  transport below and try again.
                </p>
              </div>
            </div>
          )
        )}
        <TransportPicker
          value={transport.value}
          // Boundary narrowing: step-up re-auth shows neither
          // non-transport card (pureid_qr is enrollment-only;
          // `any` is opt-in per surface and off here), so these
          // are defensive no-ops — but they keep the
          // EnrollmentChoice→PasskeyTransport conversion explicit
          // and local.
          onChange={(c) => {
            if (isPasskeyTransport(c)) transport.set(c);
          }}
          onContinue={(c) => {
            if (isPasskeyTransport(c)) void run(c);
          }}
          // After a verify failure the picker stays mounted —
          // the user needs to retry or switch transport.
          // Reframe the title so they read this as a retry
          // surface, not a fresh-choice one. (The legacy
          // "How would you like to verify?" headline read as
          // "we're starting over from scratch" which was
          // confusing in the failure context.)
          title={
            errorMessage || classification
              ? "Try a different passkey"
              : "How would you like to verify?"
          }
          subtitle={
            errorMessage || classification
              ? "Your previous attempt didn't work. Pick another option below — or the same one if you'd like to retry."
              : "Pick the device that holds your passkey."
          }
          submitLabel="Verify"
        />
      </div>
    );
  } else if (probing) {
    bodyKey = "probing";
    bodyContent = <ProbingPlaceholder />;
  } else {
    bodyKey = "streamlined";
    bodyContent = (
      <StreamlinedVerifyBody
        transport={transport.value}
        onVerify={() => void run(transport.value)}
        onSwitchDevice={() => setShowPicker(true)}
      />
    );
  }

  // Wrap the variant body in a keyed motion.div inside
  // AnimatePresence. Three motion concerns layered together:
  //
  //  1. mode="wait" sequences exit → enter — no visual overlap
  //     between the four body variants. (popLayout was
  //     considered: it would smooth the height shift this
  //     mode causes, but at the cost of letting the exiting
  //     element briefly OVERLAP the entering one — a
  //     streamlined Verify button hovering over the picker's
  //     first card during recovery reads as "something is
  //     broken". The layout-height animation below smooths the
  //     height shift without overlap, so wait+layout is the
  //     right combination.)
  //
  //  2. The outer `motion.div layout` animates the wrapper's
  //     OWN height when the inner content changes. After
  //     mode="wait" lets the new body mount with its natural
  //     height, the wrapper detects the size change and
  //     animates to it in lockstep with the inner fade-in.
  //     Without this, the dialog jolts: streamlined ~120px
  //     exits → 1-2 frames of empty slot collapse → picker
  //     ~400px mounts and snaps the dialog up. With layout,
  //     the wrapper grows smoothly while the picker fades in,
  //     and the user perceives one coordinated arrival.
  //
  //  3. `initial={false}` on the AnimatePresence skips the
  //     enter animation for the FIRST-rendered body. The
  //     dialog's own open animation (Radix Dialog) owns the
  //     entrance; without this, the body fade-in plays AFTER
  //     the dialog finishes opening — a delayed second paint
  //     that reads as lag. Subsequent body transitions still
  //     animate.
  //
  //  4. onExitComplete bumps pickerMountTrigger so the focus
  //     useEffect re-runs once the new body — specifically
  //     the picker — has been mounted (see pickerMountTrigger
  //     doc).
  //
  // Animations are opacity-only (no transform / y-shift) on
  // purpose: transforms cause sub-pixel anti-aliasing flicker
  // on sub-200ms windows in some browsers (Chromium on
  // Windows is the worst offender). The body's content already
  // differs enough between variants to communicate the change
  // without motion-by-itself doing that work.
  const body = (
    <motion.div layout transition={layoutTransition}>
      <AnimatePresence
        mode="wait"
        initial={false}
        onExitComplete={() => setPickerMountTrigger((c) => c + 1)}
      >
        <motion.div
          key={bodyKey}
          initial={{ opacity: 0 }}
          // Per-state transition in motion/react is specified
          // inline on each animation target. The animate +
          // exit targets carry their own `transition` so the
          // user perceives the asymmetric rhythm — quick exit,
          // smooth arrival.
          animate={{ opacity: 1, transition: bodyEnter }}
          exit={{ opacity: 0, transition: bodyExit }}
        >
          {bodyContent}
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );

  return (
    <GradientDialog
      open={store.pending}
      onOpenChange={(o: boolean) => {
        if (!o) handleCancel();
      }}
      tone="warning"
    >
      <DialogHeader>
        <div className="mb-3">
          <IconBadge icon={ShieldCheck} tone="warning" />
        </div>
        <DialogTitle>Verify it's you</DialogTitle>
        <DialogDescription>
          This action requires a fresh passkey verification.{" "}
          {store.reason ? reasonText(store.reason) : null}
        </DialogDescription>
        {/*
          DD-2 device-hint chip — shown only on the PICKER state,
          so the user gets the familiar "on your Mac with Touch
          ID" framing at the moment they're deciding which
          transport to use. Hidden during:
          - the running prompt (mid-ceremony noise),
          - the streamlined Verify view (the transport-aware
            button label already conveys the decision; the chip
            would be either redundant — same recommendation as
            the cascade winner — or conflicting — chip says
            phone, button says this-device because localStorage
            overrode the recommender. Either way the chip adds
            zero value on a one-option surface and creates
            cognitive load. Architect-review item A3 closed by
            this gate.),
          - the probing placeholder (we don't know the transport
            yet so the chip's "recommended for your device"
            framing has no anchor).

          Animated to match the body crossfade so the chip's
          appearance doesn't pop in / out abruptly when the user
          transitions streamlined ↔ picker. Same asymmetric
          enter/exit timing as the body so the chip and body
          read as one coherent surface change. The outer
          `motion.div layout` smooths the header height change
          when the chip mounts / unmounts — without it the
          header would jolt up by ~36px the instant showPicker
          flips, before the chip even started fading in.
        */}
        <motion.div layout transition={layoutTransition}>
          <AnimatePresence initial={false}>
            {recommendedDefault && running === null && showPicker ? (
              <motion.div
                key="chip"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, transition: bodyEnter }}
                exit={{ opacity: 0, transition: bodyExit }}
                className="mt-3 flex justify-center"
              >
                <RecommendedDeviceChip recommended={recommendedDefault} />
              </motion.div>
            ) : null}
          </AnimatePresence>
        </motion.div>
      </DialogHeader>

      <div className="px-1 py-2">{body}</div>

      <DialogFooter>
        {/* Cancel stays enabled while a ceremony is running.
            A slow begin() round-trip or a WebAuthn prompt the user
            wants to abandon shouldn't trap them; a late click
            rejects the pending promise cleanly and the store's
            internal `if (!pending)` guards ignore the in-flight
            ceremony's resolution. */}
        <Button variant="ghost" onClick={handleCancel} className="w-full">
          Cancel
        </Button>
      </DialogFooter>
    </GradientDialog>
  );
}

function reasonText(reason: string): string {
  switch (reason) {
    case "policy_required":
      return "Your tenant's security policy requires it for this operation.";
    case "session_expired":
      return "Your verified session has expired.";
    case "sensitive_action":
      return "This action is classified as sensitive.";
    // §4.5 restricted-session uplift: the banner-initiated
    // step-up has no associated mutation — the goal is to
    // lift the restriction so the user can do things. The
    // copy reflects "this clears the limit", not "this
    // unblocks an action".
    case "restricted-session.uplift":
      return "You signed in with an authenticator app. Verifying with a passkey lifts the temporary restriction on sensitive actions.";
    default:
      return "";
  }
}

// stepUpFailureCopy maps a backend stepup-* error code to the
// inline error banner copy shown in the dialog when the verify
// endpoint rejects the assertion. The user's main session is
// still valid; this is "try again with a different passkey,"
// not "sign in again."
function stepUpFailureCopy(code: string): string {
  switch (code) {
    case "stepup-assertion-rejected":
      return "That passkey didn't work. Try again — if you have multiple passkeys for this account, pick a different one or switch transport below.";
    case "stepup-user-mismatch":
      return "That passkey belongs to a different account. Pick a passkey for the account you signed in with.";
    case "stepup-rate-limited":
      return "Too many attempts in a short time. Wait a minute, then try again.";
    default:
      return "Verification failed. Try again or switch transport below.";
  }
}

// ProbingPlaceholder — the brief (<100ms typical) placeholder
// rendered while the WebAuthn capability snapshot is still
// resolving. The streamlined view's per-transport button label
// AND the useCeremonyTransport cascade's viability gate both
// depend on the resolved snapshot; rendering the streamlined
// view before resolution would flicker the button label
// mid-render when the late-arrival useEffect re-runs the
// cascade with capability in hand. The placeholder is
// intentionally minimal (no spinner-shimmer, just reserved
// height) — at typical probe latency a flashing spinner is
// more noisy than the brief blank for sighted users.
//
// Accessibility: the visually-hidden status text gives a screen
// reader something to announce during the otherwise-silent
// 50-100ms probe window. Without this, a screen reader user
// hears the dialog title + description and then nothing,
// which reads as "the dialog is broken." The sr-only span
// resolves that without adding visual noise.
// THE SAME FURNITURE, NOT A BLANK BOX.
//
// This was an empty 128px div, so the dialog opened empty and then popped into a button. That is the
// third shape a step-up ceremony could take, after the streamlined body and the picker, and shape is
// how somebody recognises a genuine passkey prompt — an empty box teaches nothing and looks like a
// broken dialog while it shows.
//
// It also is not always brief. `probing` is true while the capability snapshot resolves AND whenever
// the chosen transport is not viable on this device (see its definition above), so a device the
// cascade cannot satisfy sat on the blank box rather than on something a person could read.
//
// Now it renders the streamlined body's structure with the button disabled: same height, same
// alignment, same hint slot, nothing moving when the probe lands except the label and the enabled
// state.
function ProbingPlaceholder(): ReactNode {
  const hintId = "stepup-probing-hint";
  return (
    <div className="flex flex-col items-stretch gap-3 pt-1" aria-busy="true" aria-live="polite">
      <Button disabled className="h-12 w-full justify-center text-base" aria-describedby={hintId}>
        <ShieldCheck className="mr-2 h-5 w-5" aria-hidden />
        Preparing verification…
      </Button>
      <p id={hintId} className="text-center text-xs text-muted-foreground">
        Checking which passkey options this device supports.
      </p>
      {/* Occupies the switch-device link's row so the body does not change height when it appears. */}
      <span aria-hidden className="mx-auto text-xs font-medium text-transparent select-none">
        Use another device
      </span>
    </div>
  );
}

// StreamlinedVerifyBody — the post-2026-05-26 default idle state
// for the step-up dialog. The user has an active session (we know
// who they are), the four-layer cascade in useCeremonyTransport
// has already resolved their preferred transport, and the only
// thing standing between a click and a successful verify is a
// fresh WebAuthn ceremony. Match the Okta / Auth0 / Entra step-up
// UX: a single primary button + a small "switch device" link.
//
// The primary button supplies the fresh user activation that
// gates navigator.credentials.get — no auto-trigger on mount,
// which would race the activation timer on slow networks. One
// click is the floor; we don't try to make it zero.
//
// Copy is transport-aware so the user knows what kind of prompt
// they're about to see: the OS picker for "this device", a QR
// scan for "another device", or the security key prompt. No
// surprise modals.
interface StreamlinedVerifyBodyProps {
  transport: PasskeyTransport;
  onVerify: () => void;
  onSwitchDevice: () => void;
}

function StreamlinedVerifyBody({
  transport,
  onVerify,
  onSwitchDevice,
}: StreamlinedVerifyBodyProps): ReactNode {
  const { Icon, label, hint, switchLabel } = streamlinedAffordanceFor(transport);
  // Tie the hint paragraph to the button so screen readers
  // hear "Verify with this device, [hint copy]" as a single
  // description rather than reading them as disconnected
  // elements. aria-describedby is the canonical hook.
  const hintId = "stepup-streamlined-hint";
  return (
    <div className="flex flex-col items-stretch gap-3 pt-1">
      <Button
        onClick={onVerify}
        className="h-12 w-full justify-center text-base"
        aria-describedby={hintId}
      >
        <Icon className="mr-2 h-5 w-5" aria-hidden />
        {label}
        <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
      </Button>
      <p id={hintId} className="text-center text-xs text-muted-foreground">
        {hint}
      </p>
      <button
        type="button"
        onClick={onSwitchDevice}
        className="mx-auto text-xs font-medium text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
      >
        {switchLabel}
      </button>
    </div>
  );
}

// streamlinedAffordanceFor returns the per-transport primary-
// button + hint + switch-link copy + icon for the streamlined
// verify body. The vocabulary mirrors the TransportPicker cards
// so a user who has seen the picker recognizes the choice; the
// icon is the same glyph each picker card uses so the visual
// continuity stays across modes.
//
// switchLabel is motivation-oriented rather than descriptive — a
// user clicking "Use another device" only does so because the
// current device isn't working for them ("phone is dead",
// "loaner laptop with no key"). Framing the link as "Don't have
// X handy?" makes it discoverable for the users who need it
// without competing with the primary action for users who don't.
function streamlinedAffordanceFor(transport: PasskeyTransport): {
  Icon: typeof KeyRound;
  label: string;
  hint: string;
  switchLabel: string;
} {
  switch (transport) {
    case "this_device":
      return {
        Icon: ShieldCheck,
        label: "Verify with this device",
        // Cover the cross-platform OS picker surface honestly:
        // macOS (Touch ID, iCloud Keychain), Windows (Hello,
        // 3rd-party manager), Android (Google PM), Linux
        // (browser-driven). "saved passkey" is the umbrella
        // term that covers everything else.
        hint: "Your browser will prompt for Windows Hello, Touch ID, your password manager, or your saved passkey.",
        switchLabel: "Don't have this device handy?",
      };
    case "other_device":
      return {
        Icon: Smartphone,
        label: "Verify with your phone",
        hint: "We'll show a QR code. Scan it with your phone's camera, then approve on the phone.",
        switchLabel: "Phone not handy?",
      };
    case "security_key":
      return {
        Icon: Usb,
        // The picker card says "security key or smart card"
        // for completeness (PIV / smart-card surfaces share
        // the same WebAuthn handler), but on the streamlined
        // button "security key" is the dominant case and the
        // shorter label reads better. The hint covers both
        // tap (NFC) and plug-in (USB) without claiming all
        // keys do both.
        label: "Verify with a security key",
        hint: "Plug in your security key, or tap it on an NFC reader, when prompted.",
        switchLabel: "Don't have your security key?",
      };
  }
}
